import { AxiosError } from 'axios';

export type LmsLaunchErrorPresentation = {
  title: string;
  message: string;
  supportHint?: string;
  canRetry: boolean;
  code?: string;
};

const LMS_ERROR_COPY: Record<
  string,
  Omit<LmsLaunchErrorPresentation, 'code'> & { canRetry?: boolean }
> = {
  LMS_JWT_INVALID_SIGNATURE: {
    title: 'Sign-in link invalid',
    message: 'This launch link could not be verified. Please start again from your learning portal.',
    canRetry: true,
  },
  LMS_JWT_EXPIRED: {
    title: 'Sign-in link expired',
    message: 'This launch link has expired. Open Tamil Katral Maiyam again from your learning portal.',
    canRetry: true,
  },
  LMS_JWT_BAD_ISSUER: {
    title: 'School configuration issue',
    message: 'Launch verification failed (issuer mismatch). Contact your school administrator.',
    supportHint: 'Reference error code LMS_JWT_BAD_ISSUER when contacting support.',
    canRetry: false,
  },
  LMS_JWT_BAD_AUDIENCE: {
    title: 'School configuration issue',
    message: 'Launch verification failed (audience mismatch). Contact your school administrator.',
    supportHint: 'Reference error code LMS_JWT_BAD_AUDIENCE when contacting support.',
    canRetry: false,
  },
  LMS_JWT_MALFORMED: {
    title: 'Sign-in link invalid',
    message: 'The launch token is malformed. Please try again from your learning portal.',
    canRetry: true,
  },
  LMS_CLAIMS_INVALID: {
    title: 'Sign-in link invalid',
    message: 'The launch token is missing required information. Please try again from your learning portal.',
    canRetry: true,
  },
  LMS_JWT_UNSUPPORTED_SIGNING: {
    title: 'Sign-in link invalid',
    message: 'This launch link uses an unsupported signing method. Please start again from your learning portal.',
    canRetry: true,
  },
  LMS_JWKS_FETCH_FAILED: {
    title: 'Sign-in temporarily unavailable',
    message: 'We could not verify the launch keys from your learning portal. Please try again shortly.',
    supportHint: 'Reference error code LMS_JWKS_FETCH_FAILED when contacting support.',
    canRetry: true,
  },
  LMS_JWKS_KEY_NOT_FOUND: {
    title: 'Sign-in link invalid',
    message: 'Launch verification failed (signing key not found). Please start again from your learning portal.',
    supportHint: 'Reference error code LMS_JWKS_KEY_NOT_FOUND when contacting support.',
    canRetry: true,
  },
  LMS_INTEGRATION_DISABLED: {
    title: 'LMS sign-in unavailable',
    message: 'LMS integration is temporarily disabled on the platform.',
    canRetry: false,
  },
  LMS_SCHOOL_LMS_DISABLED: {
    title: 'School not enabled',
    message: 'LMS launch is not enabled for this school. Contact your school administrator.',
    canRetry: false,
  },
  LMS_PROVIDER_MISMATCH: {
    title: 'School configuration issue',
    message: 'The learning portal provider does not match this school configuration.',
    canRetry: false,
  },
  LMS_ROLE_NOT_ALLOWED: {
    title: 'Role not allowed',
    message: 'Your role is not permitted to launch Tamil Katral Maiyam for this school.',
    canRetry: false,
  },
  LMS_ENV_ALLOWLIST_DENIED: {
    title: 'School not in pilot',
    message: 'This school is not included in the current LMS pilot rollout.',
    canRetry: false,
  },
  LMS_SCHOOL_SETTINGS_NOT_FOUND: {
    title: 'School not found',
    message: 'We could not find LMS settings for this school. Contact your school administrator.',
    canRetry: false,
  },
  LMS_USER_NOT_LINKED: {
    title: 'Account not linked',
    message:
      'Your Tamil Katral Maiyam account is not linked yet. Ask your school administrator to invite you or confirm your email matches your portal account.',
    supportHint: 'Provide your portal email and school name to support.',
    canRetry: false,
  },
  LMS_NONCE_REPLAY: {
    title: 'Launch link already used',
    message:
      'This LMS launch link was already used. Please open a fresh link from your LMS.',
    canRetry: false,
  },
  LMS_MAPPING_CONFLICT: {
    title: 'Account linking conflict',
    message: 'Your portal identity conflicts with an existing platform link. Contact your school administrator.',
    supportHint: 'Reference error code LMS_MAPPING_CONFLICT when contacting support.',
    canRetry: false,
  },
  LMS_EMAIL_MATCH_AMBIGUOUS: {
    title: 'Ambiguous account',
    message:
      'Multiple memberships match your email for this school. Contact your school administrator to resolve your account.',
    canRetry: false,
  },
  AUTH_MISSING_TOKEN: {
    title: 'Gateway configuration',
    message: 'The sign-in gateway rejected the request. If this persists, contact support.',
    canRetry: true,
  },
};

export function extractApiErrorCode(error: unknown): string | undefined {
  if (!(error instanceof AxiosError)) return undefined;
  const data = error.response?.data as
    | { error?: { code?: string; message?: string }; error_code?: string }
    | undefined;
  return data?.error?.code ?? data?.error_code ?? undefined;
}

export const LMS_MISSING_TOKEN_PRESENTATION: LmsLaunchErrorPresentation = {
  title: 'Invalid launch link',
  message: 'Invalid LMS launch link. Token is missing.',
  canRetry: false,
};

export function getLmsLaunchErrorPresentation(error: unknown): LmsLaunchErrorPresentation {
  const code = extractApiErrorCode(error);
  if (code && LMS_ERROR_COPY[code]) {
    const copy = LMS_ERROR_COPY[code];
    return {
      code,
      title: copy.title,
      message: copy.message,
      supportHint: copy.supportHint,
      canRetry: copy.canRetry ?? false,
    };
  }

  if (error instanceof AxiosError) {
    const status = error.response?.status ?? 0;
    if (status === 401 || status === 403) {
      return {
        title: 'LMS authentication failed',
        message: 'LMS authentication failed. Please open the link again from your LMS.',
        canRetry: true,
        code,
      };
    }
    if (error.message === 'Network Error') {
      return {
        title: 'Unable to validate LMS session',
        message: 'Unable to validate LMS session. Please try again.',
        canRetry: true,
      };
    }
    const msg =
      (error.response?.data as { error?: { message?: string } })?.error?.message ??
      (typeof error.response?.data === 'object' &&
      error.response?.data &&
      'message' in error.response.data &&
      typeof (error.response.data as { message: string }).message === 'string'
        ? (error.response.data as { message: string }).message
        : undefined);
    if (msg) {
      return {
        title: 'Unable to validate LMS session',
        message: 'Unable to validate LMS session. Please try again.',
        canRetry: status >= 500,
        code,
      };
    }
  }

  if (error instanceof Error && error.message) {
    return {
      title: 'Unable to validate LMS session',
      message: 'Unable to validate LMS session. Please try again.',
      canRetry: false,
    };
  }

  return {
    title: 'Unable to validate LMS session',
    message: 'Unable to validate LMS session. Please try again.',
    canRetry: true,
  };
}
